import NodeSummary from './NodeSummary';
import React, {useContext, useMemo} from 'react';
import {NodeProps, Position} from 'reactflow';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {LoadingButton} from '@mui/lab';

import {getNodeColor, getNodeDisplay, isTerminalNode, isTriggerNode} from '../config/nodeCategories';
import FlowHandle from './FlowHandle';
import NodeAppendButton, {APPEND_BUTTON_CLASS} from './NodeAppendButton';
import {NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {Settings} from "@mui/icons-material";
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {UnreachableNodesContext} from './UnreachableNodesContext';
import {NodeActionsContext} from './NodeActionsContext';
import {TestRunSucceededContext, TestRunTrailContext} from './TestRunTrailContext';
import PreviewOverlay from '../preview/PreviewOverlay';
import {useTranslation} from "react-i18next";


const CustomNode: React.FC<NodeProps> = ({id, data, selected}) => {
    const {t} = useTranslation();
    const actions = useContext(NodeActionsContext);
    const manualRun = data.type === NodeType.MANUAL_TRIGGER ? actions?.getManualRun(id) : undefined;
    // A stored node whose type this build does not know still renders — as a neutral card.
    const nodeConfig = useMemo(() => getNodeDisplay(data.type), [data.type]);
    const isTrigger = useMemo(() => isTriggerNode(data.type), [data.type]);
    // Saving and going live stay silent about steps no trigger reaches, so the card says it.
    const unreachable = useContext(UnreachableNodesContext).has(id) && !isTrigger;
    const nodeColor = useMemo(() => getNodeColor(data.type), [data.type]);
    const testRunMark = useContext(TestRunTrailContext).get(id);
    const testRunSucceeded = useContext(TestRunSucceededContext);

    // A canvas without actions (the template preview) has no dialog to open, so no gear either.
    const onEdit = actions?.onEdit;

    // Same as the gear, for viewers too. Stopped here: the canvas's own double-click opens the add-node picker.
    const handleDoubleClick = (event: React.MouseEvent) => {
        if (!onEdit) return;

        event.stopPropagation();
        onEdit(data);
    };

    return (
        <Paper
            elevation={selected ? 4 : 1}
            onDoubleClick={handleDoubleClick}
            sx={{
                padding: 2,
                borderRadius: 2,
                border: `2px solid ${selected ? nodeColor : 'transparent'}`,
                // Drawn outside the card, so it never moves anything or meets the selection border.
                ...(testRunMark && {
                    outline: theme => `2px solid ${testRunMark === 'failed' ? theme.palette.error.main : testRunSucceeded ? theme.palette.success.main : theme.palette.warning.main}`,
                    outlineOffset: '3px',
                }),
                width: 220,
                backgroundColor: 'background.paper',
                transition: 'box-shadow 160ms ease, border-color 160ms ease',
                '@media (prefers-reduced-motion: reduce)': {transition: 'none'},
                '&:hover': {
                    boxShadow: 3,
                    [`& .${APPEND_BUTTON_CLASS}`]: {opacity: 1}
                },
                [`&:has(.${APPEND_BUTTON_CLASS}:hover)`]: {
                    boxShadow: selected ? 4 : 1
                }
            }}
        >
            {!isTrigger && (
                <FlowHandle
                    type="target"
                    position={Position.Top}
                    color={nodeColor}
                />
            )}

            <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{color: nodeColor, display: 'flex', flexShrink: 0, '& .MuiSvgIcon-root': {fontSize: 20}}}>
                    {nodeConfig.icon}
                </Box>
                <Box flex={1} minWidth={0}>
                    <Typography component="h6" variant="body1" title={data.label || t(nodeConfig.label)} sx={{
                        fontWeight: 600,
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        overflowWrap: 'anywhere',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                    }}>
                        {data.label || t(nodeConfig.label)}
                    </Typography>
                </Box>
                {unreachable && (
                    <Tooltip
                        title={t('Not connected to a trigger, so this step never runs. Connect it, or the step above it, to a trigger.')}
                        arrow
                    >
                        <ErrorOutlineRoundedIcon
                            color="error"
                            fontSize="small"
                            role="img"
                            aria-label={t('Not connected to a trigger')}
                            sx={{flexShrink: 0}}
                        />
                    </Tooltip>
                )}
                {onEdit && (
                    <Box sx={{position: 'relative', width: 28, height: 20, flexShrink: 0}}>
                        <IconButton
                            size="small"
                            aria-label={t('Configure {{name}}', {name: data.label || t(nodeConfig.label)})}
                            className="nodrag nopan"
                            sx={{position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                                width: 28, height: 28, color: 'text.secondary',
                                '&::before': {content: '""', position: 'absolute', inset: -8, borderRadius: '50%'}}}
                            onClick={event => {
                                event.stopPropagation();
                                onEdit(data);
                            }}
                        >
                            <Settings sx={{fontSize: 18}}/>
                        </IconButton>
                    </Box>
                )}
            </Stack>

            {/* Preview numbers lie over the summary, so the card's height never changes; unreachable steps get none. */}
            <Box sx={{position: 'relative'}}>
                <NodeSummary type={data.type} config={data.config}/>
                {!unreachable && <PreviewOverlay nodeId={id} type={data.type}/>}
            </Box>

            {manualRun && (
                <Tooltip title={manualRun.disabledReason || (manualRun.hasUnsavedChanges
                    ? t('Your changes will be saved before the run starts.') : '')}>
                    <Box className="nodrag nopan" sx={{mt: 1.5}} onDoubleClick={event => event.stopPropagation()}>
                        <LoadingButton
                            fullWidth
                            size="small"
                            variant="outlined"
                            loading={manualRun.isRunning}
                            disabled={manualRun.disabled}
                            onClick={event => {
                                event.stopPropagation();
                                manualRun.onRequestRun();
                            }}
                        >
                            {t('Run for everyone')}
                        </LoadingButton>
                    </Box>
                </Tooltip>
            )}

            {/* A terminal step has no output: nothing can be drawn or appended below it. */}
            {data.type !== NodeType.IF_CONDITION && !isTerminalNode(data.type) && (
                <>
                    <FlowHandle
                        type="source"
                        position={Position.Bottom}
                        color={nodeColor}
                    />
                    <NodeAppendButton nodeId={id} color={nodeColor}/>
                </>
            )}

            {/* No Yes/No text on If branches: the handle colours carry it (AUTOMATIONS_UI.md, Decisions). */}
            {data.type === NodeType.IF_CONDITION && (
                <>
                    <FlowHandle
                        type="source"
                        position={Position.Bottom}
                        id="true"
                        color="#10b981"
                        style={{left: '30%'}}
                    />
                    <FlowHandle
                        type="source"
                        position={Position.Bottom}
                        id="false"
                        color="#ef4444"
                        style={{left: '70%'}}
                    />
                    <NodeAppendButton
                        nodeId={id}
                        handleId="true"
                        color="#10b981"
                        offset="30%"
                    />
                    <NodeAppendButton
                        nodeId={id}
                        handleId="false"
                        color="#ef4444"
                        offset="70%"
                    />
                </>
            )}

        </Paper>
    );
};

export default CustomNode;
